// @flow
import React, { useContext, type Node } from 'react';
import { omit } from 'lodash';

import { withRouter } from 'Client/router/withRouter';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import * as scrollToHash from 'Components/utils/scroll/scrollToHash';
import { convertLocationToURI } from 'Utils/uri/uriUtils';
import URI from 'urijs';
import { Link } from 'Routes/nextRoutes';
import DynamicLinkUtil from './DynamicLinkUtil';

export type Props = {|
  children?: Node,
  className?: string,
  'data-selector'?: string,
  'data-pagename'?: string,
  onClick?: (SyntheticEvent<>) => *,
  target?: string,
  url?: string,
  rel?: string,
  propagateHashParams?: boolean,
  isExternalLink?: boolean,
  queryParamsToIgnore?: $ReadOnlyArray<string>,
  'data-testid'?: string,
  shallow?: boolean,
|};

export type PropsWithLocation = {
  ...Props,
  location: RouterLocation<{}, {}>,
};

const NON_DOM_ATTRIBUTES = [
  'url',
  'propagateHashParams',
  'location',
  'history',
  'match',
  'isExternalLink',
  'queryParamsToIgnore',
];

const defaultProps = {
  'data-selector': 'DynamicLink',
  url: '#',
  propagateHashParams: false,
  queryParamsToIgnore: [],
};

function getClickBehaviour(
  dynamicLink: DynamicLinkUtil,
  props: PropsWithLocation,
  scrollUnderNav: boolean,
) {
  const { onClick } = props;
  const hasHash = dynamicLink.hashToSamePage();

  if (!onClick && !hasHash) {
    return null;
  }

  return function(event) {
    if (onClick) {
      onClick(...arguments);
    }

    if (hasHash) {
      if (event && event.preventDefault) {
        event.preventDefault();
      }
      scrollToHash.goToHash(dynamicLink.hash(), scrollUnderNav);
    }
  };
}

const removeQueryParamsToIgnoreQuery = (queryString, params) => {
  const completeQuery = new URI(queryString);
  // $FlowFixMe defaultProps flow can't handle functional components
  params.forEach(element => {
    completeQuery.removeQuery(element);
  });
  return completeQuery.readable();
};

function getDynamicProps(
  dynamicLink: DynamicLinkUtil,
  props: PropsWithLocation,
  scrollUnderNav: boolean,
) {
  const toHref = removeQueryParamsToIgnoreQuery(dynamicLink.toString(), props.queryParamsToIgnore);
  const newProps = omit(
    {
      ...props,
      onClick: getClickBehaviour(dynamicLink, props, scrollUnderNav),
      route: toHref,
      ...(dynamicLink.isExternalUrl() || props.target === '_blank'
        ? { target: '_blank', rel: [props.rel, 'noopener noreferrer'].filter(Boolean).join(' ') }
        : null),
    },
    NON_DOM_ATTRIBUTES,
  );
  return newProps;
}

function DynamicLink(props: PropsWithLocation) {
  const { isDesktop } = useContext(ResponsiveContext);
  const scrollUnderNav = !isDesktop;

  if (props.url === null) {
    return null;
  }
  if (props.url === '') {
    // There are some places in the cms (at least in the mocks) where there are link with empty
    // href, which doesn't work with next. So we render a dummy span instead
    return <span {...omit(props, NON_DOM_ATTRIBUTES)} />;
  }

  const currentUri = convertLocationToURI(props.location);
  const options = { propagateHashParams: props.propagateHashParams };
  const dynamicLink = new DynamicLinkUtil(currentUri, props.url, options);

  const { route, shallow, ...dynamicProps } = getDynamicProps(dynamicLink, props, scrollUnderNav);

  return props.url !== '#' && dynamicLink.isSpaUrl() && !props.isExternalLink ? (
    <Link route={route} shallow={shallow}>
      <a {...dynamicProps} href={route} />
    </Link>
  ) : (
    <a {...dynamicProps} href={props.url !== '#' ? route : props.url} />
  );
}

DynamicLink.defaultProps = defaultProps;

export default withRouter(DynamicLink);
